"use client";

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const products = [
{
  name: '監視器',
  nameEn: 'Patient Monitor',
  desc: '提供多參數生命體徵即時監測，包括心電圖、血氧、血壓、體溫等，適用於手術室、ICU 及普通病房。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>

},
{
  name: '麻醉機',
  nameEn: 'Anaesthesia Machine',
  desc: '精確控制麻醉氣體混合及輸送，配備完善的安全監測系統，確保手術期間患者安全。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>

},
{
  name: '呼吸器',
  nameEn: 'Ventilator',
  desc: '支援多種通氣模式，適用於重症加護、急救及術後恢復，提供精準的呼吸支援。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>

},
{
  name: '去顫儀',
  nameEn: 'Defibrillator',
  desc: '提供自動體外及手動除顫功能，快速有效應對心臟驟停及心律不整緊急情況。',
  icon:
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>

}];


export default function LifeSupportSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="life-support" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div className="space-y-8">
            <div className="reveal-on-scroll">
              <span className="section-label">生命資訊與支援 · Life Information & Support</span>
            </div>
            <div className="reveal-on-scroll reveal-delay-1">
              <div className="accent-bar w-12 mb-5" style={{ background: 'linear-gradient(90deg, #1E5F8E, #2472A4)' }} />
              <h2 className="font-display font-800 text-foreground leading-tight" style={{ fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 44px)' }}>
                生命資訊
                <br />
                <span style={{ color: 'var(--color-primary)' }}>與支援設備</span>
              </h2>
            </div>
            <p className="reveal-on-scroll reveal-delay-2 text-muted leading-relaxed" style={{ fontSize: '16px' }}>
              提供全面的生命體徵監測及生命支援設備，確保患者在各種臨床環境下得到最完善的照護。我們的產品涵蓋從手術室到加護病房的各類應用場景。
            </p>

            {/* Product list */}
            <div className="reveal-on-scroll reveal-delay-3 grid sm:grid-cols-2 gap-4">
              {products?.map((p) =>
              <div key={p?.name} className="flex gap-4 p-4 rounded-xl border border-border bg-white card-hover group">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                style={{ background: '#EBF3FA', color: '#1E5F8E' }}>
                    {p?.icon}
                  </div>
                  <div>
                    <div className="font-display font-700 text-foreground text-sm" style={{ fontWeight: 700 }}>{p?.name}</div>
                    <div className="text-muted text-xs" style={{ fontWeight: 400 }}>{p?.nameEn}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Image + detail cards */}
          <div className="reveal-on-scroll reveal-delay-2 space-y-5">
            <div className="rounded-3xl overflow-hidden shadow-card-hover" style={{ aspectRatio: '16/10' }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_109b9b81f-1772880610166.png"
                alt="Medical monitors and life support equipment in intensive care unit"
                width={700}
                height={440}
                className="w-full h-full object-cover" />
              
            </div>
            {/* Desc card */}
            <div className="p-6 rounded-2xl border border-border bg-white">
              <h4 className="font-display font-700 text-foreground mb-2" style={{ fontWeight: 700, fontSize: '16px' }}>應用場景</h4>
              <div className="flex flex-wrap gap-2">
                {['手術室 OR', '加護病房 ICU', '急診室 ER', '普通病房', '恢復室']?.map((tag) =>
                <span key={tag} className="px-3 py-1.5 rounded-lg text-xs font-600 text-primary" style={{ background: '#EBF3FA', fontWeight: 600 }}>{tag}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Expanded product descriptions */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {products?.map((p, idx) =>
          <div key={p?.name} className={`reveal-on-scroll reveal-delay-${idx + 1} p-6 rounded-2xl border border-border bg-white card-hover`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#EBF3FA', color: '#1E5F8E' }}>
                {p?.icon}
              </div>
              <h3 className="font-display font-700 text-foreground mb-1" style={{ fontWeight: 700, fontSize: '16px' }}>{p?.name}</h3>
              <p className="text-xs text-muted mb-3" style={{ fontWeight: 500 }}>{p?.nameEn}</p>
              <p className="text-muted leading-relaxed" style={{ fontSize: '13px' }}>{p?.desc}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

}